import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState, useCallback } from "react";
import { MapPin, X, Loader2, Hash } from "lucide-react";
import { getGeocodingUrl } from "@itemgo/maps";
/** UK Postcode regex — matches partial and full postcodes */
const UK_POSTCODE_RE = /^[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}$|^[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9]?$|^[A-Z]{1,2}[0-9] ?$/i;
/**
 * Address autocomplete using Photon (OpenStreetMap) API.
 * Prioritises postcode results when input looks like a UK postcode.
 */
export function AddressAutocomplete({ label, placeholder = "Enter postcode or address...", value, onSelect, onClear, country = "GB", error: propError, }) {
    const containerRef = useRef(null);
    const inputRef = useRef(null);
    const debounceRef = useRef(null);
    const [inputValue, setInputValue] = useState(value || "");
    const [suggestions, setSuggestions] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isPostcodeMode, setIsPostcodeMode] = useState(false);
    // Sync external value
    useEffect(() => {
        if (value !== undefined && value !== inputValue) {
            setInputValue(value);
        }
    }, [value]);
    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);
    /** Parse Photon feature into Suggestion */
    function parsePhotonFeature(feature, index) {
        const props = feature.properties || {};
        const [lng, lat] = feature.geometry?.coordinates || [0, 0];
        const osmKey = props.osm_key || "";
        const osmValue = props.osm_value || "";
        const name = props.name || "";
        const street = props.street || "";
        const housenumber = props.housenumber || "";
        const city = props.city || props.town || props.village || props.county || "";
        // Photon sometimes puts the postcode in 'name' when osm_value is 'postcode'
        const postcode = props.postcode || (osmValue === "postcode" ? name : "") || "";
        const countryCode = props.countrycode || "";
        const isPostcode = osmKey === "postal_code" || osmValue === "postcode" || !!name.match(/^[A-Z]{1,2}[0-9]/i);
        // If this is a postcode OSM element, format differently
        let displayName;
        if (osmValue === "postcode") {
            displayName = `${name}${city ? `, ${city}` : ""}`;
        }
        else {
            const parts = [name, housenumber ? `${housenumber} ${street}` : street, city, postcode, countryCode].filter(Boolean);
            displayName = parts.join(", ");
        }
        return {
            id: props.osm_id?.toString() || `photon-${index}`,
            displayName,
            lat,
            lng,
            street: `${housenumber ? housenumber + " " : ""}${street}`.trim() || name,
            city,
            postcode,
            country: countryCode?.toLowerCase() || "gb",
            isPostcode,
        };
    }
    /** Parse Nominatim result (array of objects) into Suggestion */
    function parseNominatimResult(item, index) {
        const name = item.name || "";
        const displayName = item.display_name || "";
        const lat = parseFloat(item.lat) || 0;
        const lng = parseFloat(item.lon) || 0;
        const type = item.type || "";
        const addresstype = item.addresstype || "";
        const isPostcode = type === "postcode" || addresstype === "postcode";
        // Extract parts from display_name: "SW1A 1AA, City of Westminster, Greater London, England, United Kingdom"
        const parts = displayName.split(",").map((s) => s.trim());
        const postcode = isPostcode ? parts[0] : "";
        const city = isPostcode ? (parts[1] || "") : (parts[Math.min(parts.length - 3, 1)] || "");
        const street = isPostcode ? "" : name;
        const country = (parts[parts.length - 1] || "").toLowerCase() === "united kingdom" ? "gb" : "";
        return {
            id: item.place_id?.toString() || `nom-${index}`,
            displayName,
            lat,
            lng,
            street,
            city,
            postcode,
            country: country || "gb",
            isPostcode,
        };
    }
    /** Search using our Nominatim server */
    async function searchNominatim(query, limit) {
        const geoUrl = getGeocodingUrl();
        if (!geoUrl)
            return [];
        const encoded = encodeURIComponent(query);
        const url = `${geoUrl}/search?q=${encoded}&limit=${limit}&format=json&countrycodes=gb`;
        const res = await fetch(url);
        if (!res.ok)
            return [];
        const data = await res.json();
        if (!Array.isArray(data))
            return [];
        return data.map((item, i) => parseNominatimResult(item, i));
    }
    const searchPlaces = useCallback(async (query) => {
        if (!query.trim() || query.length < 2) {
            setSuggestions([]);
            setShowDropdown(false);
            return;
        }
        setIsLoading(true);
        setError(null);
        const geoUrl = getGeocodingUrl();
        const normalizedQuery = query.trim().toUpperCase();
        const looksLikePostcode = UK_POSTCODE_RE.test(normalizedQuery);
        setIsPostcodeMode(looksLikePostcode);
        try {
            if (geoUrl) {
                // Use our self-hosted Nominatim — single search, cheaper
                const results = await searchNominatim(query, looksLikePostcode ? 8 : 5);
                const filtered = results.filter((s) => country ? s.country === country.toLowerCase() : true);
                setSuggestions(filtered);
                setShowDropdown(filtered.length > 0);
            }
            else {
                // Fallback to Photon public API (legacy)
                const lang = country.toLowerCase() === "gb" ? "en" : country.toLowerCase();
                const encoded = encodeURIComponent(query);
                if (looksLikePostcode) {
                    const [pcRes, genRes] = await Promise.all([
                        fetch(`https://photon.komoot.io/api/?q=${encoded}&limit=3&lang=${lang}&osm_tag=place:postcode`),
                        fetch(`https://photon.komoot.io/api/?q=${encoded}&limit=4&lang=${lang}`),
                    ]);
                    const pcData = await pcRes.json();
                    const genData = await genRes.json();
                    const pcSuggestions = (pcData.features || []).map((f, i) => ({
                        ...parsePhotonFeature(f, i),
                        isPostcode: true,
                    }));
                    const seenIds = new Set(pcSuggestions.map((s) => s.id));
                    const genSuggestions = (genData.features || [])
                        .filter((f) => {
                        const id = f.properties?.osm_id?.toString() || "";
                        return !seenIds.has(id);
                    })
                        .map((f, i) => parsePhotonFeature(f, i + pcSuggestions.length));
                    const merged = [...pcSuggestions, ...genSuggestions].filter((s) => country ? s.country === country.toLowerCase() : true);
                    setSuggestions(merged);
                    setShowDropdown(merged.length > 0);
                }
                else {
                    const url = `https://photon.komoot.io/api/?q=${encoded}&limit=5&lang=${lang}`;
                    const res = await fetch(url);
                    if (!res.ok)
                        throw new Error(`Photon error: ${res.status}`);
                    const data = await res.json();
                    const mapped = (data.features || []).map((f, i) => parsePhotonFeature(f, i));
                    const filtered = country
                        ? mapped.filter((s) => s.country === country.toLowerCase())
                        : mapped;
                    setSuggestions(filtered);
                    setShowDropdown(filtered.length > 0);
                }
            }
        }
        catch (err) {
            console.error("[AddressAutocomplete] Search error:", err);
            setSuggestions([]);
            setShowDropdown(false);
        }
        finally {
            setIsLoading(false);
        }
    }, [country]);
    const handleInputChange = (e) => {
        const newValue = e.target.value;
        setInputValue(newValue);
        if (!newValue)
            onClear?.();
        if (debounceRef.current)
            clearTimeout(debounceRef.current);
        if (newValue.length >= 2) {
            debounceRef.current = setTimeout(() => searchPlaces(newValue), 500);
        }
        else {
            setSuggestions([]);
            setShowDropdown(false);
        }
    };
    const selectSuggestion = (suggestion) => {
        setInputValue(suggestion.displayName);
        setShowDropdown(false);
        onSelect({
            line1: suggestion.street || "",
            city: suggestion.city || "",
            postcode: suggestion.postcode || "",
            country: suggestion.country || "gb",
            lat: suggestion.lat,
            lng: suggestion.lng,
            formattedAddress: suggestion.displayName,
        });
    };
    const handleClear = () => {
        setInputValue("");
        setSuggestions([]);
        setShowDropdown(false);
        setError(null);
        onClear?.();
        inputRef.current?.focus();
    };
    const inputClass = "w-full rounded-xl border border-surface-border bg-surface-input px-3 py-2.5 text-sm text-text-primary outline-none focus:ring-2 focus:ring-brand-blue/30 transition-all pr-8 disabled:opacity-50";
    return (_jsxs("div", { className: "relative space-y-1.5", ref: containerRef, children: [label && (_jsxs("label", { className: "text-xs font-semibold text-text-secondary uppercase tracking-wide flex items-center gap-1", children: [_jsx(MapPin, { size: 12 }), label] })), _jsxs("div", { className: "relative", children: [_jsx("input", { ref: inputRef, type: "text", value: inputValue, onChange: handleInputChange, placeholder: placeholder, className: `${inputClass} ${isPostcodeMode ? "border-brand-blue" : ""} ${propError ? "border-error" : ""}`, autoComplete: "off" }), isLoading && (_jsx("div", { className: "absolute right-2 top-1/2 -translate-y-1/2", children: _jsx(Loader2, { size: 14, className: "animate-spin text-brand-blue" }) })), !isLoading && inputValue && (_jsx("button", { type: "button", onClick: handleClear, className: "absolute right-2 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary", children: _jsx(X, { size: 14 }) }))] }), isPostcodeMode && inputValue.length >= 2 && (_jsxs("p", { className: "text-[10px] text-brand-blue/70 flex items-center gap-1", children: [_jsx(Hash, { size: 10 }), "Postcode detected \u2014 showing postcode results first"] })), (error || propError) && _jsx("p", { className: "text-[10px] text-red-500", children: error || propError }), showDropdown && suggestions.length > 0 && (_jsxs("div", { className: "absolute z-50 mt-1 w-full bg-white border border-surface-border rounded-xl shadow-lg max-h-60 overflow-y-auto", children: [suggestions.filter((s) => s.isPostcode).length > 0 && (_jsxs(_Fragment, { children: [_jsx("div", { className: "px-3 py-1.5 bg-surface-input border-b border-surface-border", children: _jsxs("p", { className: "text-[10px] font-semibold text-text-tertiary uppercase tracking-wide flex items-center gap-1", children: [_jsx(Hash, { size: 10 }), "Postcodes"] }) }), suggestions
                                .filter((s) => s.isPostcode)
                                .map((s) => (_jsx("button", { type: "button", onClick: () => selectSuggestion(s), className: "w-full text-left px-3 py-2.5 hover:bg-brand-blue/5 transition-colors border-b border-surface-border", children: _jsxs("div", { className: "flex items-start gap-2", children: [_jsx(Hash, { size: 14, className: "text-brand-blue mt-0.5 shrink-0" }), _jsxs("div", { className: "min-w-0", children: [_jsx("p", { className: "text-sm font-semibold text-text-primary", children: s.displayName.split(",")[0] }), _jsx("p", { className: "text-xs text-text-tertiary truncate", children: s.displayName.split(",").slice(1).join(",").trim() || "Select for exact coordinates" })] })] }) }, s.id)))] })), suggestions.filter((s) => !s.isPostcode).length > 0 && (_jsxs(_Fragment, { children: [suggestions.filter((s) => s.isPostcode).length > 0 && (_jsx("div", { className: "px-3 py-1.5 bg-surface-input border-b border-surface-border border-t-2 border-t-surface-border", children: _jsxs("p", { className: "text-[10px] font-semibold text-text-tertiary uppercase tracking-wide flex items-center gap-1", children: [_jsx(MapPin, { size: 10 }), "Addresses"] }) })), suggestions
                                .filter((s) => !s.isPostcode)
                                .map((s) => (_jsx("button", { type: "button", onClick: () => selectSuggestion(s), className: "w-full text-left px-3 py-2.5 hover:bg-brand-blue/5 transition-colors border-b border-surface-border last:border-b-0", children: _jsxs("div", { className: "flex items-start gap-2", children: [_jsx(MapPin, { size: 14, className: "text-brand-blue mt-0.5 shrink-0" }), _jsx("p", { className: "text-sm text-text-primary truncate", children: s.displayName })] }) }, s.id)))] })), _jsx("div", { className: "px-3 py-1.5 bg-surface-input border-t border-surface-border", children: _jsx("p", { className: "text-[10px] text-text-tertiary text-right", children: "Powered by OpenStreetMap" }) })] }))] }));
}
//# sourceMappingURL=AddressAutocomplete.js.map